/*
 * The Research hero follows one primary eclipse of Algol (β Persei), the eclipsing binary at the heart of the
 * observatory's timing programme. Everything here is shared by the canvas renderer and the light curve overlay.
 */

/** Algol on the sky: right ascension in hours, declination in degrees, and its out-of-eclipse V magnitude. */
export const ALGOL = { hip: 14576, ra: 3.1361, dec: 40.9556, mag: 2.12 }

/** The two comparison stars measured beside it in every frame (Hipparcos numbers from the catalogue). */
export const COMPARISONS = [
    { hip: 14817, label: 'C1', name: 'ω Per', mag: 4.61 },
    { hip: 13879, label: 'C2', name: 'π Per', mag: 4.68 }
]

/** The eclipse window, in hours, with the minimum in the middle. */
export const ECLIPSE_HOURS = 10
export const MINIMUM_AT = 5
/** The period of the binary, in hours (2.867 days). */
export const PERIOD_HOURS = 68.81
/** The night's first frame, in seconds after midnight UT: the minimum falls at 23:41:12. */
export const START_UT = 18 * 3600 + 41 * 60 + 12

/** Seconds of exposure in each frame. */
export const FRAME_SECONDS = 30

/** Magnitude scale of the light curve, brightest at the top. */
export const MAG_TOP = 1.95
export const MAG_BOTTOM = 3.55

/** Algol's V magnitude a given number of hours into the window: two thirds of its light gone at minimum. */
export const algolMagnitude = (hours: number): number => ALGOL.mag + 1.27 * Math.exp(-(((hours - MINIMUM_AT) / 1.15) ** 2))

const seeded = (seed: number): (() => number) => {
    let state = seed >>> 0

    return () => {
        state = (Math.imul(state, 1664525) + 1013904223) >>> 0

        return state / 4294967296
    }
}

export type Measurement = {
    hours: number
    mag: number
    error: number
}

/** One measurement every six minutes, with the scatter a 40 cm reflector really gets on a steady night. */
export const MEASUREMENTS: Measurement[] = (() => {
    const random = seeded(2867)

    return Array.from({ length: 100 }, (_, index) => {
        const hours = 0.05 + index * 0.1
        const scatter = (random() + random() + random() - 1.5) * 0.034

        return {
            hours,
            mag: Number((algolMagnitude(hours) + scatter).toFixed(3)),
            error: Number((0.012 + random() * 0.012).toFixed(3))
        }
    })
})()

const pad = (value: number): string => String(value).padStart(2, '0')

/** "23:41:12" for a number of hours into the window. */
export const formatUt = (hours: number): string => {
    const total = Math.floor(START_UT + hours * 3600 + 1e-6) % 86400

    return `${pad(Math.floor(total / 3600))}:${pad(Math.floor(total / 60) % 60)}:${pad(total % 60)}`
}

/** Hours into the window at which a whole UT hour falls, for the light curve's axis. */
export const hourMarks = (): { hours: number, label: string }[] => {
    const marks: { hours: number, label: string }[] = []
    const first = Math.ceil(START_UT / 3600)

    for (let hour = first; ; hour += 1) {
        const hours = (hour * 3600 - START_UT) / 3600

        if (hours > ECLIPSE_HOURS) break
        if (hour % 2 === 0) marks.push({ hours, label: `${pad(hour % 24)}h` })
    }

    return marks
}
